import {FormEvent, MutableRefObject, ReactNode, useEffect, useRef, useState} from "react";
import Popper from "popper.js";
import FilterListIcon from "@material-ui/icons/FilterList";

type FilterButtonProps = {
    formShown: boolean,
    onClick: () => void,
    popper: MutableRefObject<any>
}

function FilterButton({formShown, onClick, popper}: FilterButtonProps) {
    const btnDropdownRef = useRef(null);

    const onButtonClick = () => {
        if (!formShown) {
            /* tslint:disable-next-line */
            new Popper(btnDropdownRef.current!, popper.current!, { // NOSONAR
                placement: "bottom-end"
            });
        }
        onClick();
    };

    return (<button
            className={'py-2 px-4 focus:bg-atlantis-200 hover:bg-atlantis-200 rounded focus:outline-none ' +
                (formShown ? 'bg-atlantis-200' : '')}
            onClick={onButtonClick}
            aria-label='Filters'
            ref={btnDropdownRef}>
            <FilterListIcon/>
        </button>
    );
}

export type FilterOption = {
    label: string,
    subLabel?: string,
    component: ReactNode,
}

type FilterFormProps = {
    formShown: boolean,
    formRef: MutableRefObject<any>,
    onSubmit: (e: FormEvent) => void,
    onReset: () => void,
    filterOptions: FilterOption[],
}

const FilterForm = ({
                        formShown,
                        formRef,
                        onSubmit,
                        onReset,
                        filterOptions,
                    }: FilterFormProps) => {
    return (
        <form onSubmit={onSubmit}>
            <div
                ref={formRef}
                className={
                    (formShown ? "block " : "hidden ") +
                    "p-5 bg-white text-black text-base z-50 float-left py-2 list-none text-left rounded shadow-lg mt-1"
                }
                style={{minWidth: "550px"}}
            >
                <div className='text-lg font-medium'>
                    Filters
                </div>
                {
                    filterOptions.map((option) =>
                        <div className='pt-5 font-medium' key={option.label}>
                            <div className='pb-2'>
                                {option.label} <span className="text-xs text-gray-600">{option?.subLabel}</span>
                            </div>
                            {option.component}
                        </div>
                    )
                }

                <div className='pt-10 pb-5 flex flex-row-reverse'>
                    <div className='pl-5'>
                        <button type='submit'
                                className='bg-atlantis-200 font-light hover:bg-atlantis-300 text-white py-2 px-4 rounded'
                                style={{minWidth: '82px'}}>
                            Apply
                        </button>
                    </div>

                    <button type='button' onClick={onReset}
                            className='bg-gray-300 font-light hover:bg-gray-500 text-black py-2 px-4 rounded'
                            style={{minWidth: '82px'}}>
                        Reset
                    </button>
                </div>
            </div>
        </form>
    );
}

type MultiFilterButtonProps<FilterValue> = {
    filterValue: FilterValue,
    setFilterValue: (f: FilterValue) => void,
    emptyFilterValue: FilterValue,
    dispatchSetFilterValue: (newValue: FilterValue) => void | boolean,
    filterOptions: FilterOption[],
    /** Runs each time the form opens, once it shows, so a field inside it can take focus. */
    onOpen?: () => void,
}

export const MultiFilterButton = <FilterValue, >({
                                                     filterValue,
                                                     setFilterValue,
                                                     emptyFilterValue,
                                                     dispatchSetFilterValue,
                                                     filterOptions,
                                                     onOpen,
                                                 }: MultiFilterButtonProps<FilterValue>) => {
    const [dropdownPopoverShow, setDropdownPopoverShow] = useState(false);
    const popoverDropdownRef = useRef(null);
    const nodeRef = useRef(null);

    const handleClickOutside = (e: Event) => {
        if ((nodeRef.current as any).contains(e.target)) {
            // outside
            return;
        }
        setDropdownPopoverShow(false);
    };

    // internal filter state
    useEffect(() => {
        if (dropdownPopoverShow) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownPopoverShow]);

    useEffect(() => {
        if (dropdownPopoverShow) {
            onOpen?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownPopoverShow]);

    const onButtonToggle = () => {
        setDropdownPopoverShow(!dropdownPopoverShow);
    };

    const onSubmit = (e: FormEvent) => {
        e.preventDefault();
        const result = dispatchSetFilterValue(filterValue);
        if (result !== false) {
            setDropdownPopoverShow(false);
        }
    };

    const onReset = () => {
        setFilterValue(emptyFilterValue);
        dispatchSetFilterValue(emptyFilterValue);

        setDropdownPopoverShow(false);
    }

    return (<div ref={nodeRef} className="mx-5 py rounded">
        <FilterButton formShown={dropdownPopoverShow}
                      onClick={onButtonToggle}
                      popper={popoverDropdownRef}
        />
        <FilterForm formShown={dropdownPopoverShow}
                    formRef={popoverDropdownRef}
                    filterOptions={filterOptions}
                    onSubmit={onSubmit}
                    onReset={onReset}
        />
    </div>);
}